/** The Shopify permissions Discovr requests, in the order merchants see them:
 *  customer data, then store data (view), then store data (edit), mirroring
 *  how Shopify's own grant screen groups an app's permissions. Supplies the
 *  merchant-facing label and purpose for each scope handle.
 *
 *  The authoritative requested set is `requested_scopes` on the status
 *  response (`domain.RequiredScopes` in the backend). This catalog only names
 *  them: a scope the backend requests that is missing here still renders, via
 *  humanizeScope, rather than disappearing. */
export type PermissionCategory = 'customer_data' | 'store_view' | 'store_edit';

/** Render order of the groups. */
export const PERMISSION_CATEGORIES: readonly PermissionCategory[] = [
  'customer_data',
  'store_view',
  'store_edit',
];

export interface ShopifyPermission {
  /** Scope handle exactly as requested in the OAuth URL. */
  scope: string;
  category: PermissionCategory;
  /** Merchant-facing name, e.g. "View customer events". */
  label: string;
  /** What Discovr does with it — shown before connecting. */
  purpose: string;
}

export const SHOPIFY_PERMISSIONS: readonly ShopifyPermission[] = [
  {
    scope: 'read_customer_events',
    category: 'customer_data',
    label: 'View customer events',
    purpose: "Count visits that arrive through a creator's tracking link.",
  },
  {
    scope: 'write_pixels',
    category: 'customer_data',
    label: 'Edit web pixels',
    purpose:
      'Install the pixel that records those visits. No shopper contact details are collected.',
  },
  {
    scope: 'read_discounts',
    category: 'store_view',
    label: 'View discounts',
    purpose: "Confirm each creator's discount code is present and still active on your store.",
  },
  {
    scope: 'read_orders',
    category: 'store_view',
    label: 'View orders',
    purpose: 'Attribute sales and refunds back to the creator whose code was used.',
  },
  {
    scope: 'read_products',
    category: 'store_view',
    label: 'View products',
    purpose: 'List your products so a campaign can target specific items.',
  },
  {
    scope: 'write_discounts',
    category: 'store_edit',
    label: 'Edit discounts',
    purpose: 'Create and update the discount code assigned to each creator.',
  },
];

/** A scope the catalog doesn't name: anything that writes is an edit. */
export function categoryOf(scope: string): PermissionCategory {
  return (
    SHOPIFY_PERMISSIONS.find((p) => p.scope === scope)?.category ??
    (scope.startsWith('write_') ? 'store_edit' : 'store_view')
  );
}

export interface PermissionGrant extends ShopifyPermission {
  granted: boolean;
  /** False for a scope Shopify granted that we never asked for (app-config
   *  drift). Shown, but kept out of the "N of M granted" count so the
   *  denominator stays the size of the requested set. */
  requested: boolean;
}

/** Shopify drops `read_X` from the grant when `write_X` was granted, so a
 *  literal string comparison reports permissions as missing that the merchant
 *  did in fact approve. Mirrors ValidateScopesGranted in the Go backend. */
function isGranted(scope: string, held: ReadonlySet<string>): boolean {
  if (held.has(scope)) return true;
  return scope.startsWith('read_') && held.has(`write_${scope.slice('read_'.length)}`);
}

/** Turns a raw scope handle into a readable label, for any scope not named in
 *  SHOPIFY_PERMISSIONS. "read_themes" → "Read themes". */
export function humanizeScope(scope: string): string {
  const spaced = scope.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function parseScopeList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const scope = part.trim();
    if (scope) seen.add(scope);
  }
  return [...seen];
}

/** Catalog position for a scope, so rows read views-then-edits however the
 *  backend happens to order `requested_scopes`. Scopes absent from the catalog
 *  sort last, keeping the backend's order among themselves. */
function catalogIndex(scope: string): number {
  const i = SHOPIFY_PERMISSIONS.findIndex((p) => p.scope === scope);
  return i === -1 ? SHOPIFY_PERMISSIONS.length : i;
}

function describe(scope: string): ShopifyPermission {
  return (
    SHOPIFY_PERMISSIONS.find((p) => p.scope === scope) ?? {
      scope,
      category: categoryOf(scope),
      label: humanizeScope(scope),
      purpose: '',
    }
  );
}

/** Reconciles what the app asked for against what Shopify reported granting.
 *
 *  `requestedScopes` is the backend's own requested list; when absent (an older
 *  backend) the local catalog stands in. Driving the rows from the API rather
 *  than the catalog means a scope the backend starts requesting shows up here
 *  without a frontend release.
 *
 *  Returns `null` when the grant is unknown — no scope string at all. That is
 *  NOT an empty grant, and the UI must not render it as "0 granted": a store
 *  whose grant was never recorded would otherwise look identical to one that
 *  approved nothing. */
export function reconcilePermissions(
  grantedScopes: string | undefined | null,
  requestedScopes?: string | undefined | null,
): PermissionGrant[] | null {
  if (!grantedScopes || !grantedScopes.trim()) return null;

  const held = new Set(parseScopeList(grantedScopes));
  const requested = parseScopeList(requestedScopes);
  const requestedList =
    requested.length > 0 ? requested.map(describe) : SHOPIFY_PERMISSIONS.map((p) => p);

  const rows: PermissionGrant[] = requestedList
    .map((p) => ({
      ...p,
      granted: isGranted(p.scope, held),
      requested: true,
    }))
    .sort((a, b) => catalogIndex(a.scope) - catalogIndex(b.scope));

  // Anything Shopify granted that we never asked for — surfaced rather than
  // silently dropped, so app-config drift is visible instead of invisible.
  const asked = new Set(requestedList.map((p) => p.scope));
  for (const scope of held) {
    if (asked.has(scope)) continue;
    const impliedRead =
      scope.startsWith('write_') && asked.has(`read_${scope.slice('write_'.length)}`);
    if (impliedRead) continue;
    rows.push({ ...describe(scope), granted: true, requested: false });
  }

  return rows;
}
